import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import axios from "../../axios";
import {
  PaginationType,
  ResetApiState,
  ResetPaginationState,
  ResponseWrapper,
} from "../../redux.type";
import { queryStringGenerator } from "../../redux.util";
import { Amenities, AmenitiesStateTypes } from "./Amenities.util";

const name = "amenities";
const extraActions = createExtraActions();

const AmenitiesInitialState: AmenitiesStateTypes = {
  createAmenities: ResetApiState(null),
  resetCreateAmenities: ResetApiState(null),
  editAmenities: ResetApiState(null),
  resetEditAmenities: ResetApiState(null),
  deleteAmenities: ResetApiState(null),
  resetDeleteAmenities: ResetApiState(null),
  amenities: ResetApiState([]),
  pagedAmenities: ResetApiState(ResetPaginationState()),
  amenity: ResetApiState(null),
  resetAmenities: ResetApiState(null),
  resetStates: ResetApiState(null),
};

/**
 * ACTIONS
 */
function createExtraActions() {
  const createAmenities = createAsyncThunk<ResponseWrapper<Amenities>, any>(
    `/create-${name}`,
    async (data: any, thunkApi) =>
      axios
        .post(`/${name}`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetCreateAmenities = createAction(`/reset-create-${name}`);

  const editAmenities = createAsyncThunk<ResponseWrapper<Amenities>, any>(
    `/edit-${name}`,
    async ({ data, id }: any, thunkApi) =>
      axios
        .put(`/${name}/${id}`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetEditAmenities = createAction(`/reset-edit-${name}`);

  const deleteAmenities = createAsyncThunk<number, any>(
    `/delete-${name}`,
    async (id: any, thunkApi) =>
      axios
        .delete(`${name}/${id}`)
        .then((response) => {
          if (response.data.success) {
            return id;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error?.response?.data);
        })
  );

  const resetDeleteAmenities = createAction(`/reset-delete-${name}`);

  const getAmenities = createAsyncThunk<Amenities[], any>(
    `/get-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getPagedAmenities = createAsyncThunk<PaginationType<Amenities[]>, any>(
    `/get-paged-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}/paged${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getAmenity = createAsyncThunk<Amenities, any>(
    `/get-${name}`,
    async (id: any, thunkApi) =>
      axios
        .get(`${name}/${id}`)
        .then((response) => response.data)
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetAmenities = createAction(`/reset-get-${name}`);

  const resetStates = createAction(`/reset-states`);

  return {
    createAmenities,
    resetCreateAmenities,
    editAmenities,
    resetEditAmenities,
    deleteAmenities,
    resetDeleteAmenities,
    getAmenities,
    getPagedAmenities,
    getAmenity,
    resetAmenities,
    resetStates,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(
  builder: ActionReducerMapBuilder<AmenitiesStateTypes>
) {
  return {
    ...createAmenitiesReducer(),
    ...resetCreateAmenities(),
    ...editAmenitiesReducer(),
    ...resetEditAmenities(),
    ...deleteAmenitiesReducer(),
    ...resetDeleteAmenities(),
    ...amenitiesReducer(),
    ...pagedAmenitiesReducer(),
    ...amenityReducer(),
    ...resetAmenities(),
    ...resetStatesReducer(),
  };

  function createAmenitiesReducer() {
    return {
      ...builder.addCase(
        extraActions.createAmenities.pending,
        (state, action) => {
          state.createAmenities = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createAmenities.fulfilled,
        (state, action) => {
          state.createAmenities = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createAmenities.rejected,
        (state, action) => {
          state.createAmenities = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetCreateAmenities() {
    return {
      ...builder.addCase(extraActions.resetCreateAmenities, (state, action) => {
        state.resetCreateAmenities = ResetApiState(true);
        state.createAmenities = ResetApiState(null);
      }),
    };
  }

  function editAmenitiesReducer() {
    return {
      ...builder.addCase(
        extraActions.editAmenities.pending,
        (state, action) => {
          state.editAmenities = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.editAmenities.fulfilled,
        (state, action) => {
          state.editAmenities = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.editAmenities.rejected,
        (state, action) => {
          state.editAmenities = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetEditAmenities() {
    return {
      ...builder.addCase(extraActions.resetEditAmenities, (state, action) => {
        state.resetEditAmenities = ResetApiState(true);
        state.editAmenities = ResetApiState(null);
      }),
    };
  }

  function deleteAmenitiesReducer() {
    return {
      ...builder.addCase(
        extraActions.deleteAmenities.pending,
        (state, action) => {
          state.deleteAmenities = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.deleteAmenities.fulfilled,
        (state, action) => {
          state.deleteAmenities = {
            loading: false,
            payload: true,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.deleteAmenities.rejected,
        (state, action) => {
          state.deleteAmenities = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetDeleteAmenities() {
    return {
      ...builder.addCase(extraActions.resetDeleteAmenities, (state, action) => {
        state.resetDeleteAmenities = ResetApiState(true);
        state.deleteAmenities = ResetApiState(null);
      }),
    };
  }

  function amenitiesReducer() {
    return {
      ...builder.addCase(extraActions.getAmenities.pending, (state, action) => {
        state.amenities = {
          loading: true,
          payload: [],
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.getAmenities.fulfilled,
        (state, action) => {
          state.amenities = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getAmenities.rejected,
        (state, action) => {
          state.amenities = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function pagedAmenitiesReducer() {
    return {
      ...builder.addCase(
        extraActions.getPagedAmenities.pending,
        (state, action) => {
          state.pagedAmenities = {
            loading: true,
            payload: ResetPaginationState(),
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedAmenities.fulfilled,
        (state, action) => {
          state.pagedAmenities = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedAmenities.rejected,
        (state, action) => {
          state.pagedAmenities = {
            loading: false,
            payload: ResetPaginationState(),
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function amenityReducer() {
    return {
      ...builder.addCase(extraActions.getAmenity.pending, (state, action) => {
        state.amenity = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getAmenity.fulfilled, (state, action) => {
        state.amenity = {
          loading: false,
          payload: action.payload,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getAmenity.rejected, (state, action) => {
        state.amenity = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function resetAmenities() {
    return {
      ...builder.addCase(extraActions.resetAmenities, (state, action) => {
        state.resetAmenities = ResetApiState(true);
        state.amenity = ResetApiState(null);
      }),
    };
  }

  function resetStatesReducer() {
    return {
      ...builder.addCase(extraActions.resetStates, (state, action) => {
        state.resetStates = ResetApiState(true);

        state.createAmenities = ResetApiState(null);
        state.resetCreateAmenities = ResetApiState(null);
        state.editAmenities = ResetApiState(null);
        state.resetEditAmenities = ResetApiState(null);
        state.deleteAmenities = ResetApiState(null);
        state.resetDeleteAmenities = ResetApiState(null);
        state.amenities = ResetApiState([]);
        state.amenity = ResetApiState(null);
        state.resetAmenities = ResetApiState(null);
        state.resetAmenities = ResetApiState(null);
      }),
    };
  }
}

const AmenitiesSlice = createSlice({
  name,
  initialState: AmenitiesInitialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const AmenitiesActions = {
  ...AmenitiesSlice.actions,
  ...extraActions,
};
export const AmenitiesReducer = AmenitiesSlice.reducer;
